/**
 * Safe navigation helpers for the customer Portfolio authentication boundary.
 *
 * Return paths are carried in the login URL so an anonymous customer can
 * resume a protected deep link after authenticating. They are deliberately
 * restricted to local protected Portfolio paths; arbitrary URLs must never be
 * accepted as a redirect target.
 */

export const PORTFOLIO_RETURN_TO_PARAM = "returnTo";
const PORTFOLIO_TENANT_PARAM = "tenant";
const TENANT_SLUG_PATTERN = /^[a-z0-9-]+$/;

const PUBLIC_PORTFOLIO_PATHS = new Set([
  "/portfolio",
  "/portfolio/login",
  "/portfolio/register",
  "/portfolio/verify",
  "/portfolio/verify-email",
  "/portfolio/forgot-password",
  "/portfolio/reset-password",
  "/portfolio/logout",
]);

const PROTECTED_PORTFOLIO_PATHS = [
  /^\/portfolio\/(?:dashboard|holdings|previous-purchases|transactions|performance|settings|utility|orders|returns|messages|addresses|profile)$/,
  /^\/portfolio\/orders\/[A-Za-z0-9_-]+$/,
  /^\/portfolio\/transactions\/(?:purchase|sale|transfer)$/,
];

function safePortfolioPath(value: string | null | undefined): string | null {
  if (!value) return null;
  if (
    value.length > 2048 ||
    value !== value.trim() ||
    !value.startsWith("/") ||
    value.startsWith("//") ||
    value.startsWith("/\\") ||
    value.includes("\\") ||
    /[\u0000-\u001f\u007f]/.test(value) ||
    /%(?![0-9a-f]{2})/i.test(value)
  ) return null;

  const rawPath = value.split(/[?#]/, 1)[0];
  if (
    !rawPath ||
    rawPath.includes("%") ||
    rawPath.split("/").some((segment) => segment === "." || segment === "..")
  ) return null;

  try {
    const parsed = new URL(value, "https://portfolio.invalid");
    if (parsed.origin !== "https://portfolio.invalid") return null;
    if (
      !parsed.pathname.startsWith("/portfolio/") ||
      PUBLIC_PORTFOLIO_PATHS.has(parsed.pathname) ||
      !PROTECTED_PORTFOLIO_PATHS.some((pattern) => pattern.test(parsed.pathname))
    ) return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

/**
 * Add the tenant context already present on the current Portfolio URL to a
 * same-origin Portfolio route. The server remains the authority for whether
 * the slug exists and is enabled; this helper only carries a syntactically
 * valid value forward and never reads the persisted tenant fallback.
 */
export function portfolioPath(path: string, sourceSearch?: string): string {
  let parsed: URL;
  try {
    parsed = new URL(path, "https://portfolio.invalid");
    if (
      parsed.origin !== "https://portfolio.invalid" ||
      (parsed.pathname !== "/portfolio" && !parsed.pathname.startsWith("/portfolio/"))
    ) {
      return path;
    }
  } catch {
    return path;
  }

  const search =
    sourceSearch ??
    (typeof window !== "undefined" ? window.location.search : "");
  const tenant = new URLSearchParams(search).get(PORTFOLIO_TENANT_PARAM);
  if (tenant && TENANT_SLUG_PATTERN.test(tenant) && !parsed.searchParams.has(PORTFOLIO_TENANT_PARAM)) {
    parsed.searchParams.set(PORTFOLIO_TENANT_PARAM, tenant);
  }
  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

type PortfolioAuthPath =
  | "/portfolio/login"
  | "/portfolio/register"
  | "/portfolio/verify"
  | "/portfolio/verify-email"
  | "/portfolio/forgot-password"
  | "/portfolio/reset-password";

/** Return a public Portfolio auth URL carrying only a safe local route. */
export function portfolioAuthPath(
  path: PortfolioAuthPath,
  returnTo?: string | null,
  sourceSearch?: string,
): string {
  const safeReturnTo = safePortfolioReturnTo(returnTo);
  if (!safeReturnTo) return portfolioPath(path, sourceSearch);
  const params = new URLSearchParams({ [PORTFOLIO_RETURN_TO_PARAM]: safeReturnTo });
  return portfolioPath(`${path}?${params.toString()}`, sourceSearch);
}

/** Return a login URL carrying only a safe local Portfolio route. */
export function portfolioLoginPath(returnTo?: string | null, sourceSearch?: string): string {
  return portfolioAuthPath("/portfolio/login", returnTo, sourceSearch);
}

/** Read and validate the login page's optional post-auth return path. */
export function portfolioReturnToFromSearch(search: string): string | null {
  return safePortfolioReturnTo(new URLSearchParams(search).get(PORTFOLIO_RETURN_TO_PARAM));
}

export function safePortfolioReturnTo(value: string | null | undefined): string | null {
  return safePortfolioPath(value);
}
